import { useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { Loading } from 'react-admin';
import { useRegion } from '../auth/RegionContext';
import { NotAuthorized, RequireAccess } from '../auth/RequireAccess';
import { marketsForScope } from '../dataProvider/types';
import { formatMoney } from '../cashbacks/formatMoney';
import { useImpersonation } from './ImpersonationContext';
import { useMemberEyeView } from './useMemberEyeView';
import type { MemberEyeView } from './impersonationModel';

/**
 * View as user (D-impersonation, the Phase 6 surface-4 close). A VIEW-ONLY member's-eye
 * lens over a subject's data while an impersonation session is active. It NEVER acts as the
 * member: the only writes in the whole surface are the session START (a deliberate button
 * here) and the EXPLICIT END (in the global indicator). The member's-eye projection itself
 * writes nothing and offers no action.
 *
 * The projection is shown ONLY while a session is active for this subject (the exposure
 * window). The active session is the provider's single source of truth (derived off the
 * clock), so it clears by expiry with no write. Operational region-scoped (an agent views
 * only their region's members) and role-gated to support and super_admin.
 */

const day = (s: string): string => s.slice(0, 10);

const Block = ({
  title,
  testId,
  children,
}: {
  title: string;
  testId: string;
  children: ReactNode;
}) => (
  <Box sx={{ mb: 2 }} data-testid={testId}>
    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

const MemberEyePanel = ({
  view,
  expiresAt,
}: {
  view: MemberEyeView;
  expiresAt: string;
}) => (
  <Box data-testid="member-eye-view">
    <Chip
      size="small"
      color="warning"
      variant="outlined"
      label={`Session active until ${expiresAt.slice(0, 16).replace('T', ' ')}`}
      sx={{ mb: 2 }}
    />
    <Block title="Balance" testId="member-eye-balance-block">
      <Typography variant="h6" data-testid="member-eye-balance">
        {formatMoney(view.balance, view.currency)}
      </Typography>
    </Block>

    <Block title="Cashback" testId="member-eye-cashbacks">
      {view.cashbacks.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No cashback yet.
        </Typography>
      ) : (
        <Stack spacing={0.5}>
          {view.cashbacks.map((c) => (
            <Typography key={c.id} variant="body2">
              {c.storeName} | {formatMoney(c.grossAmount, c.currency)} order |{' '}
              {formatMoney(c.cashbackAmount, c.currency)} cashback | {c.status}{' '}
              | {day(c.occurredAt)}
            </Typography>
          ))}
        </Stack>
      )}
    </Block>

    <Block title="Cashouts" testId="member-eye-cashouts">
      {view.cashouts.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No cashouts yet.
        </Typography>
      ) : (
        <Stack spacing={0.5}>
          {view.cashouts.map((c) => (
            <Typography key={c.id} variant="body2">
              {formatMoney(c.amount, c.currency)} | {c.destinationType} |{' '}
              {c.status} | {day(c.requestedAt)}
              {c.charityName ? ` | ${c.charityName}` : ''}
            </Typography>
          ))}
        </Stack>
      )}
    </Block>

    <Block title="Plus" testId="member-eye-plus">
      <Typography variant="body2">
        {view.plus.status}
        {view.plus.currentPeriodEnd
          ? ` | renews ${day(view.plus.currentPeriodEnd)}`
          : ''}
        {view.plus.autoRenew ? ' | auto-renew on' : ''}
      </Typography>
    </Block>

    <Block title="Referrals" testId="member-eye-referrals">
      <Typography variant="body2">
        {view.referrals.referredBy
          ? `Referred by member #${view.referrals.referredBy}`
          : 'Not referred'}
        {view.referrals.rewardStatus
          ? ` | reward ${view.referrals.rewardStatus}`
          : ''}
      </Typography>
    </Block>

    <Block title="Your offers" testId="member-eye-offers">
      {view.offers.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No offers featured.
        </Typography>
      ) : (
        <Stack spacing={0.5}>
          {view.offers.map((o) => (
            <Typography key={o.id} variant="body2">
              {o.merchantName} | {o.slot}
            </Typography>
          ))}
        </Stack>
      )}
    </Block>
  </Box>
);

/** The surface without the access wrapper. */
export const ViewAsUserView = () => {
  const { userId } = useParams();
  const subjectId = Number(userId);
  const { region } = useRegion();
  const { activeSession, start } = useImpersonation();
  const { view, isLoading } = useMemberEyeView(subjectId);
  const [starting, setStarting] = useState(false);

  if (isLoading || !view) return <Loading />;

  // Operational region scope: an agent views only their region's members.
  const markets = marketsForScope(region);
  if (!markets.includes(view.member.market)) return <NotAuthorized />;

  const activeForThis = activeSession?.subjectUserId === subjectId;
  const activeForOther = activeSession != null && !activeForThis;

  const onStart = async () => {
    setStarting(true);
    try {
      await start({
        id: view.member.id,
        name: view.member.name,
        market: view.member.market,
      });
    } finally {
      setStarting(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 900, mx: 'auto' }}>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ letterSpacing: 1 }}
      >
        Support
      </Typography>
      <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 0.5 }}>
        View as user
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        data-testid="view-as-member"
      >
        {view.member.name} | {view.member.email} |{' '}
        {view.member.market.toUpperCase()}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        component="p"
        sx={{ mb: 2 }}
        data-testid="member-eye-caption"
      >
        This is the member's data as we hold it, shown through the member-facing
        projection. It is not a pixel-for-pixel copy of the member's app. You
        are viewing only. No action is ever taken as the member.
      </Typography>

      <Divider sx={{ mb: 2 }} />

      {activeForThis && activeSession ? (
        <MemberEyePanel view={view} expiresAt={activeSession.expiresAt} />
      ) : activeForOther && activeSession ? (
        <Card variant="outlined">
          <CardContent data-testid="view-as-blocked">
            <Typography variant="body2">
              You are currently viewing as {activeSession.subjectName}. End that
              session first, from the indicator in the top bar.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Start a time-limited, audited, view-only session to see this
              member's data as they would. The session auto-expires and a
              persistent indicator shows while it is active.
            </Typography>
            <Button
              variant="contained"
              color="warning"
              disabled={starting}
              onClick={() => void onStart()}
              data-testid="view-as-start"
            >
              Start viewing as {view.member.name}
            </Button>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

/**
 * Gated entry registered at /view-as/:userId. ROLE-gated to support and super_admin (a
 * capability read-gate would let the viewer in via '*:read'), like the ticket inbox.
 */
export const ViewAsUser = () => (
  <RequireAccess roles={['support', 'super_admin']}>
    <ViewAsUserView />
  </RequireAccess>
);
